<script lang="ts">
import { computed, defineComponent, inject, PropType, ref, watch } from "vue";
import Steps from "@/components/Steps.vue";
import formValidierung from "@/businesslogic/formValidierung";
import { NavvItem } from "@/model/NavItem";
import Ueberschrift from "@/components/grid/Ueberschrift.vue";
import Adresse from "@/components/person/Adresse.vue";
import Zeile from "@/components/grid/Zeile.vue";
import Zelle from "@/components/grid/Zelle.vue";
import TitelZelle from "@/components/grid/TitelZelle.vue";
import Infotext from "@/components/Infotext.vue";
import ValidierungsFehler from "@/components/ValidierungsFehler.vue";
import Logger from "js-logger";
import BauartklasseErmittlung from "@/components/bauartklasse/BauartklasseErmittlung.vue";
import Modal from "@/components/Modal.vue";
import storeUtils from "@/businesslogic/utils/storeUtils";
import gebaeudeUtils from "@/businesslogic/utils/gebaeudeUtils";
import GebaeudeTyp from "@/components/GebaeudeTyp.vue";
import IconSprite from "@/components/IconSprite.vue";
import dateUtils from "@/businesslogic/utils/dateUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

export default defineComponent({
  name: "BasisdatenFH2024",
  emits: ["prev-page", "next-page", "goto-page", "handle-error"],
  components: {
    IconSprite,
    BauartklasseErmittlung,
    Steps,
    Ueberschrift,
    Adresse,
    Zeile,
    TitelZelle,
    Zelle,
    Infotext,
    ValidierungsFehler,
    Modal,
    GebaeudeTyp,
  },
  props: {
    navItems: Object as PropType<NavvItem[]>,
    anfrageId: String,
  },
  setup(_props, { emit }) {
    const piniaStore = useFormStore();
    const istSichtbar: any = inject("istSichtbar");
    const wechsleSichtbarkeit: any = inject("wechsleSichtbarkeit");
    const feldBlurFocus: any = inject("feldBlurFocus");
    const { getIsoDatePlusYearsPlusMonths } = dateUtils();

    const fehler = ref({} as any);
    const addresseRef = ref<InstanceType<typeof Adresse>>();

    const { erzwingeGanzzahl } = formValidierung();
    const { berechneGebaeudetyp } = gebaeudeUtils();

    const {
      istNurVGV,
      istNurVHV,
      istVGVundVHV,
      enthaeltVGV,
      enthaeltVHV,
      istFerienhaus,
      istEinfamilienhaus,
      istMehrfamilienhaus,
    } = storeUtils();

    const naechstesJahr = new Date(getIsoDatePlusYearsPlusMonths(1, 3)).getFullYear();
    const baujahrFehlerText = "Bitte geben Sie das vierstellige Baujahr (1800-" + naechstesJahr + ") ein.";

    const modalMehrfamilienhausVHV = computed(() => {
      return {
        titel: piniaStore.state.isMakler
          ? "Wichtiger Hinweis zur Hausratversicherung"
          : "Hausratversicherung bei Mehrfamilienhäusern",
        text: piniaStore.state.isMakler
          ? "Es kann nur für eine Wohnung die Hausratversicherung eingeschlossen werden. Für weitere Wohnungen muss jeweils eine neue FHK-Hausratversicherung abgeschlossen werden."
          : 'Bitte beachten Sie, dass Sie in diesem Vorgang nur eine Ferienwohnung beantragen können. Möchten Sie mehrere Ferienwohnungen versichern, können Sie weitere Anträge unter der Auswahl "Ferienwohnung" einreichen. </br> Gern können Sie sich in dem Fall auch an unsere Kundenberater unter <strong><a href="tel:+494037663366">040 37663-366</a></strong> oder <span><a class="inherited" href="mailto:kundenservice@grundvers.de">kundenservice@grundvers.de</a></span> wenden.',
        hatSchliessenButton: true,
        schliessenButtonText: "OK",
      };
    });

    const eingabenSindValide = () => {
      return new Promise((resolve) => {
        const { validiereForm, getFormFehlerAnzahl, getFormFehler } = formValidierung();
        validiereForm("").then(() => {
          fehler.value = getFormFehler();
          resolve(getFormFehlerAnzahl() === 0);
        });
      });
    };

    const nextPage = () => {
      piniaStore.state.gebaeudedaten.gebaeudetyp = berechneGebaeudetyp(
        piniaStore.state.gebaeudedaten.gebaeudesubtyp,
        piniaStore.state.gebaeudedaten.anzahlWohnungen,
      );

      eingabenSindValide().then((isValid) => {
        addresseRef.value?.pruefen(true).then((isAdresseValid) => {
          Logger.debug("Valide", isValid, isAdresseValid);
          if (isValid && isAdresseValid) {
            emit("next-page", { pageIndex: 0 });
          }
        });
      });
    };

    watch(
      () => piniaStore.state.gebaeudedaten.immobilieArt,
      () => {
        if (!istFerienhaus(piniaStore.state)) {
          piniaStore.state.ferienhaus.versicherungsumfang = "0003";
          piniaStore.state.tarifinformationen.hausundgrundbesitzerhaftpflicht = false;
        }
        if (istFerienhaus(piniaStore.state) && piniaStore.state.gebaeudedaten.gebaeudesubtyp === "mfh") {
          piniaStore.state.ferienhaus.versicherungsumfang = "0002";
        }
      },
    );

    watch(
      () => piniaStore.state.ferienhaus.versicherungsumfang,
      () => {
        if (!istNurVHV(piniaStore.state)) {
          piniaStore.state.ferienhaus.vhv.tarifinformationen.buendelRabatt = true;
          piniaStore.state.ferienhaus.hug.tarifinformationen.buendelRabatt = true;
        } else {
          piniaStore.state.ferienhaus.vhv.tarifinformationen.buendelRabatt = false;
          piniaStore.state.ferienhaus.hug.tarifinformationen.buendelRabatt = false;
        }
      },
    );

    watch(
      () => piniaStore.state.gebaeudedaten.gebaeudesubtyp,
      () => {
        if (istFerienhaus(piniaStore.state) && istMehrfamilienhaus(piniaStore.state)) {
          piniaStore.state.ferienhaus.versicherungsumfang = "0002";
        } else if (istFerienhaus(piniaStore.state) && istEinfamilienhaus(piniaStore.state)) {
          piniaStore.state.ferienhaus.versicherungsumfang = "0001";
        }
      },
    );

    watch(
      () => [piniaStore.state.gebaeudedaten.immobilieArt, piniaStore.state.ferienhaus.versicherungsumfang],
      () => {
        if (
          istFerienhaus(piniaStore.state) &&
          istMehrfamilienhaus(piniaStore.state) &&
          istVGVundVHV(piniaStore.state)
        ) {
          wechsleSichtbarkeit("modalMehrfamilienhausVHV");
        }
      },
    );

    return {
      piniaStore,
      nextPage,
      feldBlurFocus,
      fehler,
      istSichtbar,
      wechsleSichtbarkeit,
      erzwingeGanzzahl,
      baujahrFehlerText,
      addresseRef,
      modalMehrfamilienhausVHV,
      istNurVGV,
      istNurVHV,
      istVGVundVHV,
      enthaeltVGV,
      enthaeltVHV,
      istFerienhaus,
      istEinfamilienhaus,
      istMehrfamilienhaus,
      naechstesJahr,
    };
  },
});
</script>

<template>
  <steps :active="0" v-bind:items="navItems" @goto-page="$emit('goto-page', $event)" />

  <section>
    <ueberschrift>Angaben zur Versicherung</ueberschrift>
    <adresse
      :fehler="fehler"
      :placeholder="true"
      v-model:plz="piniaStore.state.risikoAnschrift.plz"
      v-model:ort="piniaStore.state.risikoAnschrift.ort"
      v-model:strasse="piniaStore.state.risikoAnschrift.strasse"
      v-model:hausnummer="piniaStore.state.risikoAnschrift.hausnummer"
      v-model:zu-pruefen="piniaStore.state.risikoAnschrift.zuPruefen"
      :feldNamen="{
        plz: 'risikoPostalCode',
        ort: 'risikoCity',
        strasse: 'risikoStreet',
        hausnummer: 'risikoStreetNumber',
      }"
      ref="addresseRef"
      session-marker="risikoAnschrift"
    >
      <template v-slot:plzOrtLabel>
        <p class="u-text-align-special">
          <strong>Anschrift</strong> der Immobilie
          <span class="c-notice-icon" @click="wechsleSichtbarkeit('infoAnschrift')">
            <icon-sprite icon-name="icon-info-i" />
          </span>
        </p>
      </template>
      <template v-slot:strasseHausnummerLabel><span class="mobil-hidden">&nbsp;</span></template>
    </adresse>
    <zeile>
      <div
        class="o-grid__cell o-grid__cell--no-space o-grid__cell--12 o-grid__cell--6-1024 o-grid__cell--offset-4-1024"
      >
        <infotext
          name="infoAnschrift"
          css-klassen="o-grid__cell--no-space"
          text="Diese Angaben werden für die Beitragsberechnung benötigt."
        >
        </infotext>
      </div>
    </zeile>
    <zeile>
      <titel-zelle><strong>Örtliche Lage</strong> der Immobilie</titel-zelle>
      <zelle class="mkRadio">
        <input
          v-model.number="piniaStore.state.ferienhaus.oertlicheLage"
          type="radio"
          id="innerhalbOrtschaft"
          name="oertlicheLage"
          :value="1"
          required
          aria-required="true"
          aria-label="Immobilie befindet sich innerhalb einer geschlossenen Ortschaft"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl"
          data-cy="OertlicheLageInnerhalb"
        />
        <label class="radiolabel" for="innerhalbOrtschaft">Innerhalb geschlossener Ortschaft</label><br />
        <input
          v-model.number="piniaStore.state.ferienhaus.oertlicheLage"
          type="radio"
          id="außerhalbOrtschaft"
          name="oertlicheLage"
          :value="2"
          aria-label="Immobilie befindet sich ausserhalb einer geschlossenen Ortschaft"
          data-cy="OertlicheLageAußerhalb"
        />
        <label class="radiolabel" for="außerhalbOrtschaft">Außerhalb geschlossener Ortschaft</label>
        <validierungs-fehler :fehler-text="fehler.oertlicheLage" :reduzierter-abstand="true" />
      </zelle>
    </zeile>
    <zeile>
      <titel-zelle :zeige-info="true" info-box-id="hausverwaltung"
        >Gebäude durch <strong>Hausverwaltung</strong> kontrolliert?
      </titel-zelle>
      <zelle class="mkRadio">
        <input
          v-model="piniaStore.state.ferienhaus.kontrolleHausverwaltung"
          type="radio"
          id="keineHausverwaltung"
          name="hausverwaltung"
          :value="false"
          required
          aria-required="true"
          aria-label="Nein, das Gebäude wird nicht durch eine Hausverwaltung kontrolliert"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl."
          data-cy="hausverwaltungRadioNo"
        />

        <label for="keineHausverwaltung" class="radiolabel">Nein</label>
        <input
          v-model="piniaStore.state.ferienhaus.kontrolleHausverwaltung"
          type="radio"
          id="hausverwaltung"
          name="hausverwaltung"
          :value="true"
          aria-label="Ja, das Gebäude wird durch eine Hausverwaltung kontrolliert"
          data-cy="hausverwaltungRadioYes"
        />
        <label for="hausverwaltung" class="radiolabel">Ja</label>
        <validierungs-fehler :fehler-text="fehler.hausverwaltung" :reduzierter-abstand="true" />
        <infotext
          name="hausverwaltung"
          css-klassen="o-grid__cell--no-space"
          text="Wird die regelmäßige Kontrolle des Objektes durch eine Fremdfirma durchgeführt?"
        >
        </infotext>
      </zelle>
    </zeile>
    <zeile>
      <titel-zelle><strong>Art der Immobilie</strong></titel-zelle>
      <zelle class="mkRadio">
        <input
          v-model="piniaStore.state.gebaeudedaten.immobilieArt"
          type="radio"
          id="ferienhaus"
          name="immobilieArt"
          value="1"
          required
          aria-required="true"
          aria-label="Ferienhaus"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl"
          data-cy="ImmobilieArtFerienhaus"
        />
        <label for="ferienhaus" class="radiolabel">Ferienhaus</label>
        <input
          v-model="piniaStore.state.gebaeudedaten.immobilieArt"
          type="radio"
          id="ferienwohnung"
          name="immobilieArt"
          value="2"
          aria-label="Ferienwohnung"
          data-cy="ImmobilieArtFerienwohnung"
        />
        <label for="ferienwohnung" class="radiolabel">Ferienwohnung</label>
        <validierungs-fehler :fehler-text="fehler.immobilieArt" :reduzierter-abstand="true" />
      </zelle>
    </zeile>
    <gebaeude-typ :fehler="fehler" v-if="istFerienhaus(piniaStore.state)" />

    <zeile v-if="istFerienhaus(piniaStore.state)">
      <titel-zelle><strong>Was möchten Sie versichern?</strong></titel-zelle>
      <zelle>
        <select
          id="versicherungsumfang"
          name="versicherungsumfang"
          v-model="piniaStore.state.ferienhaus.versicherungsumfang"
          class="c-form-control-select"
          data-fehlermeldung="Bitte treffen Sie eine Auswahl."
          data-cy="versicherungsumfang"
          aria-label="Was möchten Sie versichern?"
        >
          <option value="0001">Wohngebäude- und Hausratversicherung</option>
          <option value="0002">Wohngebäudeversicherung</option>
          <option :disabled="istMehrfamilienhaus(piniaStore.state)" value="0003">Hausratversicherung</option>
        </select>
        <validierungs-fehler :fehler-text="fehler.versicherungsumfang" :reduzierter-abstand="true" />
      </zelle>
    </zeile>
    <zeile v-if="piniaStore.state.isMakler === false && istNurVHV(piniaStore.state)">
      <zelle :breite="6" :offset="4">
        <div class="u-copy-s gev19-infotext--grey">
          Unsere Hausratversicherung gilt für Häuser und Wohnungen, bei denen es sich um Massiv- oder Fertighäuser mit
          hartem Dach handelt.
        </div>
      </zelle>
    </zeile>

    <ueberschrift v-if="istFerienhaus(piniaStore.state) && enthaeltVGV(piniaStore.state)"
      >Angaben zur Immobilie</ueberschrift
    >

    <zeile v-if="istFerienhaus(piniaStore.state) && enthaeltVGV(piniaStore.state)">
      <titel-zelle><strong>Baujahr</strong> des Gebäudes</titel-zelle>
      <zelle :breite="3">
        <input
          type="number"
          id="baujahr"
          name="baujahr"
          v-model.lazy.trim.number="piniaStore.state.gebaeudedaten.baujahr"
          @keydown="erzwingeGanzzahl"
          @keyup="feldBlurFocus($event, 900)"
          class="c-form-control__input"
          data-cy="baujahr"
          @change="checkBaujahr"
          min="1800"
          :max="naechstesJahr"
          :required="istFerienhaus(piniaStore.state) && enthaeltVGV(piniaStore.state)"
          :aria-required="istFerienhaus(piniaStore.state) && enthaeltVGV(piniaStore.state)"
          :data-fehlermeldung="baujahrFehlerText"
          placeholder="JJJJ"
          aria-label="Baujahr des Gebäudes"
        />
        <validierungs-fehler :fehler-text="fehler.baujahr" />
      </zelle>
    </zeile>
    <BauartklasseErmittlung
      v-if="(istFerienhaus(piniaStore.state) && enthaeltVGV(piniaStore.state)) || piniaStore.state.isMakler"
      :fehler="fehler"
    />
    <zeile>
      <zelle :breite="5" :offset="4">
        <button
          class="c-button c-button--full-width u-mar-t-2 u-mar-b-2 mobil-mehrAbstand"
          @click="nextPage"
          data-cy="submitZuImmobiliendetails"
        >
          Weiter zu Immobilien-Details
        </button>
      </zelle>
    </zeile>
  </section>
  <Modal
    v-if="istSichtbar('modalMehrfamilienhausVHV')"
    @closeModal="wechsleSichtbarkeit('modalMehrfamilienhausVHV')"
    :titel="modalMehrfamilienhausVHV.titel"
    :hat-schliessen-button="modalMehrfamilienhausVHV.hatSchliessenButton"
    :schliessen-button-text="modalMehrfamilienhausVHV.schliessenButtonText"
  >
    <template v-slot:inhalt>
      <span v-html="modalMehrfamilienhausVHV.text"></span>
    </template>
  </Modal>
</template>
